import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

const categories = [
  { key: "all", label: "All" },
  { key: "multifamily", label: "Multifamily Completed" },
  { key: "multifamily-dev", label: "Multifamily Development" },
  { key: "homes", label: "Custom Homes" },
  { key: "kitchens", label: "Custom Kitchens" },
  { key: "showers", label: "Custom Showers & Tile" },
  { key: "construction", label: "Under Construction" },
  { key: "plans", label: "Development & Plans" },
  { key: "details", label: "Design Details" },
  { key: "outdoors", label: "Outdoors & Landscape" },
];

const photos = [
  // Multifamily Completed
  { src: "/images/project-apartment.jpg", title: "Apartment Community", sub: "Multifamily — Completed", cat: "multifamily" },
  { src: "/images/project-westwave.jpg", title: "West Wave", sub: "Multifamily — Completed", cat: "multifamily" },
  { src: "/images/project-residences.jpg", title: "Modern Residences", sub: "Multifamily — Completed", cat: "multifamily" },
  { src: "/images/project-residence.jpg", title: "Multifamily Residence", sub: "Multifamily — Completed", cat: "multifamily" },
  { src: "/images/project-townhomes.jpg", title: "Townhome Community", sub: "Multifamily — Completed", cat: "multifamily" },
  { src: "/images/project-aerial.jpg", title: "Apartment Community — Aerial", sub: "Multifamily — Completed", cat: "multifamily" },
  { src: "/images/project-ridgeview.jpg", title: "Apartment Residences — Ridge View", sub: "Multifamily — Completed", cat: "multifamily" },
  { src: "/images/project-northcourt.jpg", title: "Modern Apartment Residence", sub: "Multifamily — Completed", cat: "multifamily" },
  // Multifamily Development
  { src: "/images/construction-multifamily.webp", title: "The Community, Rising", sub: "Wood-Frame Multifamily", cat: "multifamily-dev" },
  { src: "/images/construction-slab.webp", title: "Structure Meets Sky", sub: "Slab, Steel & Crane", cat: "multifamily-dev" },
  { src: "/images/comm-cross-section.jpg", title: "Multifamily Building Cross Section", sub: "Plans & Renderings", cat: "multifamily-dev" },
  // Custom Homes
  { src: "/images/feat_e.jpg", title: "Custom Home Design", sub: "New Construction — Complete", cat: "homes" },
  { src: "/images/lux_a.jpg", title: "The Craftsman Retreat", sub: "Custom Home — Complete", cat: "homes" },
  { src: "/images/feat_a.jpg", title: "Gated Entrance", sub: "Stone & Iron Entry", cat: "homes" },
  { src: "/images/dev-garage-1.webp", title: "The Garage Wing", sub: "Timber, Stone & Carriage Doors", cat: "homes" },
  { src: "/images/feat_d.jpg", title: "Rear Terrace", sub: "Hot Tub & Gazebo", cat: "homes" },
  { src: "/images/lux_c.jpg", title: "Timber Lodge", sub: "Custom Log Build", cat: "homes" },
  { src: "/images/dev-garage-2.webp", title: "Stone by Stone", sub: "Garage Elevation Detail", cat: "homes" },
  { src: "/images/feat_c.jpg", title: "Garage Wing", sub: "Stone & Timber Detail", cat: "homes" },
  { src: "/images/lux_b.jpg", title: "Estate, From Above", sub: "Development & Landscape", cat: "homes" },
  { src: "/images/feat_b.jpg", title: "Site Overview", sub: "Aerial Perspective", cat: "homes" },
  { src: "/images/lux_d.jpg", title: "Timber Lodge — Aerial", sub: "Aerial Site Overview", cat: "homes" },
  // Custom Kitchens
  { src: "/images/kitchen-island.webp", title: "The Gathering Island", sub: "Waterfall Island & Pendant Lighting", cat: "kitchens" },
  { src: "/images/kitchen-range.webp", title: "The Chef's Range", sub: "Marble Backsplash & Pro Appliances", cat: "kitchens" },
  { src: "/images/project-kitchen.jpg", title: "Apartment Interiors", sub: "Cabinetry, Quartz & Stainless", cat: "kitchens" },
  { src: "/images/detail-herringbone.webp", title: "The Herringbone Kitchen", sub: "Custom Tile & Backsplashes", cat: "kitchens" },
  { src: "/images/detail-galley.webp", title: "The Galley, Tiled", sub: "Custom Tile & Backsplashes", cat: "kitchens" },
  { src: "/images/detail-hexagon.webp", title: "Hex & Oak", sub: "Custom Tile & Backsplashes", cat: "kitchens" },
  { src: "/images/detail-mosaicisland.webp", title: "The Mosaic Island", sub: "Custom Tile & Backsplashes", cat: "kitchens" },
  // Custom Showers & Tile
  { src: "/images/detail-marble.webp", title: "Marble & Walnut", sub: "Spa Baths", cat: "showers" },
  { src: "/images/detail-honeycomb.webp", title: "The Honeycomb Bath", sub: "Custom Tile Showers", cat: "showers" },
  { src: "/images/detail-niche.webp", title: "Matte Black & Niche", sub: "Custom Tile Showers", cat: "showers" },
  { src: "/images/detail-bench.webp", title: "The Rainfall Suite", sub: "Custom Tile Showers", cat: "showers" },
  { src: "/images/detail-subway.webp", title: "Subway, Perfected", sub: "Custom Tile Showers", cat: "showers" },
  { src: "/images/detail-granite.webp", title: "Carved in Stone", sub: "Steam Showers & Spa Baths", cat: "showers" },
  { src: "/images/detail-steam.webp", title: "Steam, On Schedule", sub: "Steam Showers", cat: "showers" },
  { src: "/images/detail-tub.webp", title: "Tub & Surround", sub: "Custom Tile Showers", cat: "showers" },
  { src: "/images/detail-pebble.webp", title: "River Stone Underfoot", sub: "Custom Tile Showers", cat: "showers" },
  { src: "/images/detail-travertine.webp", title: "Stone & Pebble", sub: "Custom Tile Showers", cat: "showers" },
  { src: "/images/detail-pebbleinset.webp", title: "Pebble Inset", sub: "Custom Tile Showers", cat: "showers" },
  { src: "/images/detail-wavewall.webp", title: "The Wave Wall", sub: "Custom Tile Showers", cat: "showers" },
  { src: "/images/detail-marblefloor.webp", title: "Mirror-Polish Marble", sub: "Custom Tile & Flooring", cat: "showers" },
  { src: "/images/detail-hallway.webp", title: "Stone & Hardwood", sub: "Custom Tile & Flooring", cat: "showers" },
  // Under Construction
  { src: "/images/construction-siteprep.jpg", title: "Breaking New Ground", sub: "Site Preparation & Earthwork", cat: "construction" },
  { src: "/images/construction-rebar.webp", title: "Foundations, Laid Right", sub: "Rebar & Slab Preparation", cat: "construction" },
  { src: "/images/construction-formwork.webp", title: "Formed to the Millimetre", sub: "Concrete Formwork", cat: "construction" },
  { src: "/images/construction-steel.webp", title: "The Skeleton, Standing", sub: "Steel & Concrete Cores", cat: "construction" },
  { src: "/images/dev-progress-1.webp", title: "Raised & Roofed", sub: "Custom Home — In Progress", cat: "construction" },
  { src: "/images/dev-progress-2.webp", title: "The Welcome, Framed", sub: "Entry & Stonework — In Progress", cat: "construction" },
  // Development & Plans
  { src: "/images/dev-site-plan.jpg", title: "100 Home Master Residential Development Plan", sub: "Architectural Site Drawing", cat: "plans" },
  // Design Details
  { src: "/images/detail-theater.webp", title: "The Private Cinema", sub: "Home Theater Automation", cat: "details" },
  { src: "/images/detail-mirror.webp", title: "The Mirror, Reimagined", sub: "TV Automated Mirrors", cat: "details" },
  { src: "/images/detail-fireplace.webp", title: "The Hearth, Reimagined", sub: "Feature Walls & Fireplaces", cat: "details" },
  { src: "/images/detail-stackedstone.webp", title: "The See-Through Hearth", sub: "Feature Walls & Fireplaces", cat: "details" },
  { src: "/images/detail-mantel.webp", title: "Slate & Mantel", sub: "Feature Walls & Fireplaces", cat: "details" },
  { src: "/images/detail-oakmantel.webp", title: "Marble & Oak", sub: "Feature Walls & Fireplaces", cat: "details" },
  { src: "/images/detail-midnight.webp", title: "Midnight Marble", sub: "Feature Walls & Fireplaces", cat: "details" },
  { src: "/images/detail-laminate.webp", title: "Plank by Plank", sub: "Flooring & Finishes", cat: "details" },
  // Outdoors & Landscape
  { src: "/images/landscape-3.jpg", title: "Pergola & Lawn Design", sub: "Landscape Design — Custom Build", cat: "outdoors" },
  { src: "/images/landscape-2.jpg", title: "Curved Retaining Wall", sub: "Hardscape & Plantings", cat: "outdoors" },
  { src: "/images/landscape-1.jpg", title: "Paver Patio Installation", sub: "Excavation & Base Work", cat: "outdoors" },
];

const PER_PAGE = 8;

const Frame = ({ p, onOpen }) => (
  <motion.div
    layout
    initial={{ opacity: 0, scale: 0.94 }}
    animate={{ opacity: 1, scale: 1 }}
    exit={{ opacity: 0, scale: 0.94 }}
    transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
  >
    <figure
      onClick={onOpen}
      className="group relative overflow-hidden border border-[#D4AF37]/15 bg-[#111111] h-[220px] sm:h-[260px] lg:h-[280px] cursor-zoom-in"
      data-testid={`portfolio-frame-${p.src.split("/").pop().split(".")[0]}`}
    >
      <div className="h-full overflow-hidden">
        <img
          src={p.src}
          alt={p.title}
          loading="lazy"
          className="w-full h-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-[1.06]"
        />
      </div>
      <span className="absolute top-0 left-0 w-8 h-8 border-t border-l border-[#D4AF37]/70 m-3 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
      <span className="absolute bottom-0 right-0 w-8 h-8 border-b border-r border-[#D4AF37]/70 m-3 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
    </figure>
    <div className="mt-3.5 flex flex-wrap items-baseline gap-x-4 gap-y-2">
      <span data-testid="attribution-tag" className="text-[8px] tracking-[0.28em] uppercase text-[#A3A3A3] border border-[#D4AF37]/25 px-2 py-[3px]">
        {p.tag || "Team Experience"}
      </span>
      <span className="text-[9px] tracking-[0.3em] uppercase text-[#D4AF37]">{p.sub}</span>
      <span className="font-display text-base sm:text-lg text-[#F5F5F5]">{p.title}</span>
    </div>
  </motion.div>
);

const Lightbox = ({ photo, onClose, onPrev, onNext }) => {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") onPrev();
      if (e.key === "ArrowRight") onNext();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose, onPrev, onNext]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-sm flex items-center justify-center p-4 sm:p-10"
      onClick={onClose}
      data-testid="portfolio-lightbox"
    >
      <button
        onClick={onClose}
        data-testid="lightbox-close"
        aria-label="Close"
        className="absolute top-5 right-5 text-[#D4AF37] border border-[#D4AF37]/40 p-2.5 hover:bg-[#D4AF37] hover:text-black transition-colors duration-300 z-10"
      >
        <X size={20} />
      </button>
      <button
        onClick={(e) => { e.stopPropagation(); onPrev(); }}
        data-testid="lightbox-prev"
        aria-label="Previous photo"
        className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 text-[#D4AF37] border border-[#D4AF37]/40 p-2.5 hover:bg-[#D4AF37] hover:text-black transition-colors duration-300 z-10"
      >
        <ChevronLeft size={22} />
      </button>
      <button
        onClick={(e) => { e.stopPropagation(); onNext(); }}
        data-testid="lightbox-next"
        aria-label="Next photo"
        className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 text-[#D4AF37] border border-[#D4AF37]/40 p-2.5 hover:bg-[#D4AF37] hover:text-black transition-colors duration-300 z-10"
      >
        <ChevronRight size={22} />
      </button>
      <motion.figure
        key={photo.src}
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
        className="max-w-full max-h-full flex flex-col items-center"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={photo.src}
          alt={photo.title}
          className="max-w-[92vw] max-h-[78vh] object-contain border border-[#D4AF37]/25"
          data-testid="lightbox-image"
        />
        <figcaption className="mt-5 flex flex-wrap items-baseline justify-center gap-x-5 gap-y-2" data-testid="lightbox-caption">
          <span data-testid="lightbox-attribution-tag" className="text-[9px] tracking-[0.3em] uppercase text-[#A3A3A3] border border-[#D4AF37]/25 px-2.5 py-1">
            {photo.tag || "Team Experience"}
          </span>
          <span className="text-[10px] tracking-[0.35em] uppercase text-[#D4AF37]">{photo.sub}</span>
          <span className="font-display text-xl sm:text-2xl text-[#F5F5F5]">{photo.title}</span>
        </figcaption>
      </motion.figure>
    </motion.div>
  );
};

export default function Portfolio() {
  const [active, setActive] = useState("all");
  const [page, setPage] = useState(0);
  const [lightbox, setLightbox] = useState(null);

  const visible = active === "all" ? photos : photos.filter((p) => p.cat === active);
  const totalPages = Math.max(1, Math.ceil(visible.length / PER_PAGE));
  const safePage = Math.min(page, totalPages - 1);
  const paged = visible.slice(safePage * PER_PAGE, safePage * PER_PAGE + PER_PAGE);
  const count = (key) => (key === "all" ? photos.length : photos.filter((p) => p.cat === key).length);

  const selectTab = (key) => {
    setActive(key);
    setPage(0);
    setLightbox(null);
  };

  return (
    <section id="portfolio" className="relative py-28 lg:py-40 border-t border-[#D4AF37]/10" data-testid="portfolio-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="mb-14 lg:mb-16 lg:flex lg:items-end lg:justify-between">
          <div>
            <motion.p
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.8 }}
              className="text-[11px] tracking-[0.45em] uppercase text-[#D4AF37] mb-8"
              data-testid="portfolio-overline"
            >
              01 — The Portfolio
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.9, delay: 0.1 }}
              className="font-display text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.05] tracking-tight text-[#F5F5F5]"
              data-testid="portfolio-headline"
            >
              Every craft.
              <br />
              <span className="italic text-[#D4AF37]">One gallery.</span>
            </motion.h2>
          </div>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, delay: 0.2 }}
            className="mt-8 lg:mt-0 max-w-sm text-sm font-light leading-relaxed tracking-wide text-[#A3A3A3]"
          >
            Selected work from our team's portfolio — kitchens, showers,
            communities, and custom homes crafted throughout our careers.
            Photos marked "Team Experience" represent work completed by our
            people prior to Veritas, spanning Vancouver Island, British
            Columbia — Victoria, Parksville, Qualicum Beach, Duncan, Nanaimo,
            Ucluelet and Campbell River.
          </motion.p>
        </div>

        <div className="mb-12 lg:mb-14 flex flex-wrap justify-center gap-3" data-testid="portfolio-tabs" role="tablist">
          {categories.map((c) => (
            <button
              key={c.key}
              role="tab"
              aria-selected={active === c.key}
              onClick={() => selectTab(c.key)}
              data-testid={`portfolio-tab-${c.key}`}
              className={`px-5 sm:px-6 py-3 text-[10px] sm:text-[11px] tracking-[0.24em] uppercase transition-colors duration-300 border ${
                active === c.key
                  ? "bg-[#D4AF37] text-black border-[#D4AF37] font-medium"
                  : "border-[#D4AF37]/25 text-[#A3A3A3] hover:border-[#D4AF37]/60 hover:text-[#D4AF37]"
              }`}
            >
              {c.label}
              <span className={`ml-2 ${active === c.key ? "text-black/60" : "text-[#D4AF37]/60"}`}>{count(c.key)}</span>
            </button>
          ))}
        </div>

        <motion.div layout className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-7" data-testid="portfolio-grid">
          <AnimatePresence mode="popLayout">
            {paged.map((p) => (
              <Frame key={p.src} p={p} onOpen={() => setLightbox(visible.indexOf(p))} />
            ))}
          </AnimatePresence>
        </motion.div>

        {totalPages > 1 && (
          <div className="mt-12 flex items-center justify-center gap-6" data-testid="portfolio-pagination">
            <button
              onClick={() => setPage(Math.max(0, safePage - 1))}
              disabled={safePage === 0}
              data-testid="portfolio-page-prev"
              aria-label="Previous page"
              className="text-[#D4AF37] border border-[#D4AF37]/40 p-2.5 hover:bg-[#D4AF37] hover:text-black disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-[#D4AF37] transition-colors duration-300"
            >
              <ChevronLeft size={18} />
            </button>
            <span className="text-[11px] tracking-[0.3em] uppercase text-[#A3A3A3]" data-testid="portfolio-page-info">
              {safePage + 1} <span className="text-[#D4AF37]">/</span> {totalPages}
            </span>
            <button
              onClick={() => setPage(Math.min(totalPages - 1, safePage + 1))}
              disabled={safePage === totalPages - 1}
              data-testid="portfolio-page-next"
              aria-label="Next page"
              className="text-[#D4AF37] border border-[#D4AF37]/40 p-2.5 hover:bg-[#D4AF37] hover:text-black disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-[#D4AF37] transition-colors duration-300"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        )}
      </div>

      <AnimatePresence>
        {lightbox !== null && visible[lightbox] && (
          <Lightbox
            photo={visible[lightbox]}
            onClose={() => setLightbox(null)}
            onPrev={() => setLightbox((lightbox - 1 + visible.length) % visible.length)}
            onNext={() => setLightbox((lightbox + 1) % visible.length)}
          />
        )}
      </AnimatePresence>
    </section>
  );
}
